/**
 * App Component
 * Root component that sets up routing, context providers, and error handling
 */

import { useState, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
import { Layout } from './components/layout/Layout';
import { AuthProvider } from './contexts/AuthContext';
import { GridProvider } from './contexts/GridContext';
import { CreditsProvider } from './contexts/CreditsContext';
import { TutorialProvider } from './contexts/TutorialContext';
import { useTutorial } from './contexts/TutorialContext';
import { Home } from './pages/Home';
import type { Composition } from './types/composition';

// Lazy load route components for code splitting
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const Terms = lazy(() => import('./pages/Terms').then(module => ({ default: module.Terms })));
const Admin = lazy(() => import('./pages/Admin').then(module => ({ default: module.Admin })));
const SharedComposition = lazy(() => import('./pages/SharedComposition').then(module => ({ default: module.SharedComposition })));
const NotFound = lazy(() => import('./pages/NotFound').then(module => ({ default: module.NotFound })));

// Lazy load tutorial components for better initial load performance
const TutorialOverlay = lazy(() => import('./components/tutorial/TutorialOverlay').then(module => ({ default: module.TutorialOverlay })));
const TutorialErrorBoundary = lazy(() => import('./components/tutorial/TutorialErrorBoundary').then(module => ({ default: module.TutorialErrorBoundary })));

/**
 * Loading fallback component
 */
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-accent text-sm">Loading...</div>
    </div>
  );
}

/**
 * TutorialWrapper Component
 * 
 * Wraps the TutorialOverlay with an error boundary and lazy loading.
 * Provides access to tutorial context for error handling.
 * Only loads tutorial components when tutorial is active.
 */
function TutorialWrapper() {
  const { isActive, exitTutorial } = useTutorial();

  // Don't render anything if tutorial is not active (lazy loading optimization)
  if (!isActive) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <TutorialErrorBoundary onError={exitTutorial}>
        <TutorialOverlay />
      </TutorialErrorBoundary>
    </Suspense>
  );
}

/**
 * App component
 * Main application component with routing and global state management
 */
function App() {
  // State for shared composition loading
  const [sharedComposition, setSharedComposition] = useState<Composition | null>(null);

  /**
   * Handle shared composition loaded
   * This will be passed to the SharedComposition page
   */
  const handleCompositionLoaded = useCallback((composition: Composition) => {
    setSharedComposition(composition);
  }, []);

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <GridProvider>
              <CreditsProvider>
                <TutorialProvider>
                  <Layout>
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        {/* Home page - not lazy loaded for faster initial render */}
                        <Route path="/" element={<Home sharedComposition={sharedComposition} />} />
                        
                        {/* About page - lazy loaded */}
                        <Route path="/about" element={<About />} />
                        
                        {/* Contact page - lazy loaded */}
                        <Route path="/contact" element={<Contact />} />
                        
                        {/* Terms page - lazy loaded */}
                        <Route path="/terms" element={<Terms />} />
                        
                        {/* Admin page - lazy loaded */}
                        <Route path="/admin" element={<Admin />} />
                        
                        {/* Shared composition page - lazy loaded */}
                        <Route 
                          path="/share/:id" 
                          element={<SharedComposition onCompositionLoaded={handleCompositionLoaded} />} 
                        />
                        
                        {/* 404 page - lazy loaded */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </Layout>
                  
                  {/* Tutorial overlay - renders when tutorial is active */}
                  <TutorialWrapper />
                </TutorialProvider>
              </CreditsProvider>
            </GridProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
